import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import UpgradeOptionsTabs from "../components/UpgradeOptionsTabs";
import { NavigationContext, UserContext } from "../contexts";
import { getPageTitle } from "../resources/routes";
import { scrollToTop } from "../util";

const UpgradeOptionsScreen = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const [slideIndexes, setSlideIndexes] = useState<number[]>([]);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.resetSelectedUpgrades();
    });

    // If there are pre-selected upgrades, select them by default
    const preselected = userContext.upgradesWithPromoPricing.filter(
      (item) => item.preselected
    );
    for (const upgrade of preselected) {
      if (!userContext.selectedUpgrades.get(upgrade.category_id)) {
        userContext.setSelectedUpgrade(upgrade.category_id, upgrade);
      }
    }

    const _slideIndexes = [];
    for (let i = 0; i < userContext.upgradeCategories.length; i++) {
      let slideIndex = 0;

      // If there is a pre-selected upgrade, we need to have the slider show it
      const existing = userContext.selectedUpgrades.get(
        userContext.upgradeCategories[i].id
      );
      if (existing) {
        const upgradesFromCat = userContext.upgradesWithPromoPricing.filter(
          (item) => item.category_id === userContext.upgradeCategories[i].id
        );
        for (let x = 0; x < upgradesFromCat.length; x++) {
          if (upgradesFromCat[x].id === existing.id) {
            // The slider moves 2 slides at a time, so we need to make sure it lands on a number divisable by 2
            slideIndex = x;
            if (slideIndex % 2 !== 0) {
              slideIndex = x - 1;
            }
            break;
          }
        }
      }

      _slideIndexes.push(slideIndex);
    }
    setSlideIndexes(_slideIndexes);
    return () => {
      navContext.resetBackBtnOnClick();
    };
  }, []);

  useEffect(() => {
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();
    if (tabIndex < userContext.upgradeCategories.length - 1) {
      navContext.setNextBtnOnClick(() => {
        setTabIndex(tabIndex + 1);
      });
    }

    if (tabIndex > 0) {
      navContext.setBackBtnOnClick(() => {
        setTabIndex(tabIndex - 1);
      });
    }
  }, [tabIndex, userContext.upgradeCategories]);

  const onChangeSlideIndex = (tabIndex: number, index: number) => {
    const _slideIndexes = [...slideIndexes];
    _slideIndexes[tabIndex] = index;
    setSlideIndexes(_slideIndexes);
  };

  return (
    <div className={"screen screen-upgrade-options"}>
      <div className={"container"}>
        <h2 className="h1">{getPageTitle(location.pathname)}</h2>
        <p className={"subtitle"}>
          Choose from a range of inclusions options to make your new home just
          right for you.
        </p>
      </div>
      <UpgradeOptionsTabs
        categories={userContext.upgradeCategories}
        upgrades={userContext.upgradesWithPromoPricing}
        selectedUpgrades={userContext.selectedUpgrades}
        setSelectedUpgrade={(id, option) =>
          userContext.setSelectedUpgrade(id, option)
        }
        tabIndex={tabIndex}
        onChangeTabIndex={(index) => setTabIndex(index)}
        slideIndexes={slideIndexes}
        onChangeSlideIndex={onChangeSlideIndex}
      />
    </div>
  );
});

export default UpgradeOptionsScreen;
